'use client'

import type { RefObject } from 'react'
import { BriefcaseMedical, Building2, Check, ChevronRight } from 'lucide-react'
import { cn } from '@/utilities/ui'
import type { SwitcherOrgViewModel } from '@/utilities/dashboard/orgProgramSwitcherTree'
import {
  FOCUS_VISIBLE_RING,
  IconTile,
  MatchBadge,
  ROW_HOVER_CLASS,
  ScrollFade,
  SelectedBadge,
  type ScrollAffordance,
} from './OrgProgramSwitcherParts'

export type OrgProgramSwitcherDesktopProps = {
  filtered: SwitcherOrgViewModel[]
  activeOrg: SwitcherOrgViewModel | null
  selectedOrgId: string | null
  selectedProgramId: string | null
  isOrgSelected: boolean
  searchQuery: string
  hasScrollableDesktopLists: boolean
  orgListRef: RefObject<HTMLDivElement | null>
  programListRef: RefObject<HTMLDivElement | null>
  orgScrollAffordance: ScrollAffordance
  programScrollAffordance: ScrollAffordance
  updateOrgScrollAffordance: () => void
  updateProgramScrollAffordance: () => void
  onActivateOrg: (orgId: string) => void
  onSelectOrg: (orgId: string) => void
  onSelectProgram: (orgId: string, programId: string) => void
}

export function OrgProgramSwitcherDesktop({
  filtered,
  activeOrg,
  selectedOrgId,
  selectedProgramId,
  isOrgSelected,
  searchQuery,
  hasScrollableDesktopLists,
  orgListRef,
  programListRef,
  orgScrollAffordance,
  programScrollAffordance,
  updateOrgScrollAffordance,
  updateProgramScrollAffordance,
  onActivateOrg,
  onSelectOrg,
  onSelectProgram,
}: Readonly<OrgProgramSwitcherDesktopProps>) {
  return (
    <div
      className={cn(
        'hidden sm:grid sm:grid-cols-[18.5rem_minmax(0,1fr)] sm:items-stretch',
        hasScrollableDesktopLists && 'sm:h-[28rem] sm:overflow-hidden',
      )}
      data-testid="org-program-switcher-desktop"
    >
      <div className="flex h-full min-h-0 flex-col gap-4 border-r border-light-grey pr-4">
        <p className="m-0 text-xs font-medium tracking-wider text-cp-placeholder uppercase">
          Organizations
        </p>
        <div className="relative min-h-0 flex-1">
          <div
            ref={orgListRef}
            className="flex h-full flex-col gap-2 overflow-y-auto pr-1"
            data-testid="org-program-switcher-desktop-org-list"
            onScroll={updateOrgScrollAffordance}
          >
            {filtered.map((org) => {
              const isActive = org.id === activeOrg?.id
              const isSelectedOrg = selectedOrgId === org.id
              return (
                <button
                  key={org.id}
                  type="button"
                  aria-current={isSelectedOrg ? 'true' : undefined}
                  data-active={isActive ? 'true' : undefined}
                  data-selected={isSelectedOrg ? 'true' : undefined}
                  data-testid={`org-program-switcher-org-${org.id}`}
                  onClick={() => onActivateOrg(org.id)}
                  className={cn(
                    'flex w-full cursor-pointer items-center justify-between rounded-lg p-2 text-left transition-colors',
                    FOCUS_VISIBLE_RING,
                    isActive || isSelectedOrg ? 'bg-cp-light-purple' : ROW_HOVER_CLASS,
                  )}
                >
                  <span className="flex min-w-0 items-center gap-4">
                    <IconTile>
                      <Building2 className="h-5 w-5 text-cp-bright-blue" aria-hidden="true" />
                    </IconTile>
                    <span className="min-w-0">
                      <span
                        className={cn(
                          'block truncate text-sm font-medium',
                          isActive || isSelectedOrg ? 'text-cp-bright-blue' : 'text-cp-dark-blue',
                        )}
                      >
                        {org.name}
                      </span>
                      <span className="mt-0.5 flex h-4 items-center gap-2">
                        <span className="text-xs text-cp-placeholder whitespace-nowrap">
                          {org.programs.length} Program{org.programs.length === 1 ? '' : 's'}
                        </span>
                        <MatchBadge count={org.matchCount} />
                      </span>
                    </span>
                  </span>
                  <span className="ml-2 flex shrink-0 items-center gap-2">
                    {isSelectedOrg && <SelectedBadge />}
                    <ChevronRight
                      className={cn(
                        'h-4 w-4 shrink-0',
                        isActive || isSelectedOrg ? 'text-cp-dark-blue' : 'text-cp-light-grey-2',
                      )}
                      aria-hidden="true"
                    />
                  </span>
                </button>
              )
            })}
          </div>
          <ScrollFade
            edge="top"
            testId="org-program-switcher-desktop-org-list-scroll-up"
            visible={orgScrollAffordance.canScrollUp}
          />
          <ScrollFade
            edge="bottom"
            testId="org-program-switcher-desktop-org-list-scroll-down"
            visible={orgScrollAffordance.canScrollDown}
          />
        </div>
      </div>

      <div className="flex h-full min-h-0 min-w-0 flex-col gap-2 pl-5">
        <p className="m-0 mb-2 text-xs font-medium tracking-wider text-cp-placeholder uppercase">
          {activeOrg?.name ?? 'Organization'}
        </p>

        {activeOrg?.showAllPrograms && (
          <button
            type="button"
            aria-current={isOrgSelected && selectedOrgId === activeOrg.id ? 'true' : undefined}
            data-selected={isOrgSelected && selectedOrgId === activeOrg.id ? 'true' : undefined}
            data-testid="org-program-switcher-all-programs"
            onClick={() => onSelectOrg(activeOrg.id)}
            className={cn(
              'flex min-h-11 w-full cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-left transition-colors',
              FOCUS_VISIBLE_RING,
              isOrgSelected && selectedOrgId === activeOrg.id
                ? 'bg-cp-light-purple'
                : ROW_HOVER_CLASS,
            )}
          >
            <span className="flex items-center gap-4">
              <IconTile>
                <BriefcaseMedical className="h-5 w-5 text-cp-dark-blue-2" aria-hidden="true" />
              </IconTile>
              <span
                className={cn(
                  'text-sm font-medium',
                  isOrgSelected && selectedOrgId === activeOrg.id
                    ? 'text-cp-bright-blue'
                    : 'text-cp-dark-blue',
                )}
              >
                All Programs
              </span>
            </span>
            {isOrgSelected && selectedOrgId === activeOrg.id && (
              <span className="ml-2 flex shrink-0 items-center gap-2">
                <SelectedBadge />
                <Check className="h-4 w-4 text-cp-bright-blue" aria-hidden="true" />
              </span>
            )}
          </button>
        )}

        {activeOrg && activeOrg.visiblePrograms.length > 0 && (
          <p className="m-0 mt-2 text-xs font-medium tracking-wider text-cp-placeholder uppercase">
            Programs
          </p>
        )}

        <div className="relative min-h-0 flex-1">
          <div
            ref={programListRef}
            className="flex h-full flex-col gap-1 overflow-y-auto pr-1"
            data-testid="org-program-switcher-desktop-program-list"
            onScroll={updateProgramScrollAffordance}
          >
            {activeOrg?.visiblePrograms.map((program) => {
              const isSelected = selectedProgramId === program.id && selectedOrgId === activeOrg.id
              const isMatch =
                Boolean(searchQuery.trim()) &&
                activeOrg.orgMatchesSearch === false &&
                (program.name.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
                  program.id.toLowerCase().includes(searchQuery.trim().toLowerCase()))
              let programRowStateClass = ROW_HOVER_CLASS
              if (isSelected) {
                programRowStateClass = 'bg-cp-light-purple'
              } else if (isMatch) {
                programRowStateClass = 'bg-cp-beta-bg/50 hover:bg-cp-beta-bg/70'
              }
              return (
                <button
                  key={program.id}
                  type="button"
                  aria-current={isSelected ? 'true' : undefined}
                  data-selected={isSelected ? 'true' : undefined}
                  data-testid={`org-program-switcher-program-${program.id}`}
                  onClick={() => onSelectProgram(activeOrg.id, program.id)}
                  className={cn(
                    'flex min-h-11 w-full cursor-pointer items-center rounded-lg px-3 py-2 text-left transition-colors',
                    FOCUS_VISIBLE_RING,
                    programRowStateClass,
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block truncate text-sm font-medium',
                        isSelected ? 'text-cp-bright-blue' : 'text-cp-dark-blue',
                      )}
                    >
                      {program.name}
                    </span>
                  </span>
                  {isSelected && (
                    <span className="ml-2 flex shrink-0 items-center gap-2">
                      <SelectedBadge />
                      <Check className="h-4 w-4 text-cp-bright-blue" aria-hidden="true" />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          <ScrollFade
            edge="top"
            testId="org-program-switcher-desktop-program-list-scroll-up"
            visible={programScrollAffordance.canScrollUp}
          />
          <ScrollFade
            edge="bottom"
            testId="org-program-switcher-desktop-program-list-scroll-down"
            visible={programScrollAffordance.canScrollDown}
          />
        </div>
      </div>
    </div>
  )
}
